import { useState } from 'react';
import { Link } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { crmApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { notify } from '@/core/toast/notify';
import { showApiError } from '@/utils/apiError';

interface SocialCrmActionBarProps {
  sourceType: 'message' | 'comment' | 'review' | 'conversation';
  platform: string;
  preview?: string;
  customerId?: number;
  compact?: boolean;
}

export function SocialCrmActionBar({
  sourceType,
  platform,
  preview,
  customerId,
  compact,
}: SocialCrmActionBarProps) {
  const [busy, setBusy] = useState(false);

  const createLead = async () => {
    setBusy(true);
    try {
      const lead = await crmApi.createLead({
        customerId,
        leadSource: `SOCIAL_${platform}_${sourceType}`.toUpperCase(),
        leadStatus: 'NEW',
        leadScore: sourceType === 'review' ? 60 : 40,
      });
      notify.success(`Lead #${lead.id} created`);
    } catch (err) {
      showApiError(err, 'Could not create CRM lead');
    } finally {
      setBusy(false);
    }
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-1">
        <Button size="sm" variant="secondary" onClick={() => void createLead()} loading={busy}>
          Lead
        </Button>
        <Link to="/crm/deals" className="text-xs text-primary-600 hover:underline dark:text-primary-400">
          Opportunity
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-900/50">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">CRM</span>
      <Button size="sm" variant="secondary" onClick={() => void createLead()} loading={busy}>
        <PersonAddIcon sx={{ fontSize: 16 }} className="mr-1" />
        Convert to lead
      </Button>
      <Link to="/crm/deals">
        <Button size="sm" variant="ghost">
          <TrendingUpIcon sx={{ fontSize: 16 }} className="mr-1" />
          New opportunity
        </Button>
      </Link>
      <Link to="/crm/leads" className="text-xs text-primary-600 hover:underline dark:text-primary-400">
        View social leads
      </Link>
      {preview && (
        <Tooltip content={preview}>
          <p className="w-full truncate text-xs text-slate-500">
            Source: {platform} · {sourceType}
          </p>
        </Tooltip>
      )}
    </div>
  );
}
